import { Accordion, AccordionItem } from '@/components/common/accordion'
import { Button } from '@/components/common/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogContentWrapper,
  DialogFooter,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from '@/components/common/dialog'
import { Separator } from '@/components/common/separator'
import { FilterIcon } from '@/components/icons/filter'
import { PlusIcon } from '@/components/icons/plus'
import { FilterAccordionContent, FilterAccordionTrigger } from '@/components/shared/filterSelect'
import { FilterListContent } from '@/components/shared/filterSelectContent'
import { useFixedTermMarketsSearchState } from '@/hooks/useFixedTermMarketsSearchState'
import { cn } from '@/utils/cn'
import type { FixedTermMarket } from '@/utils/internalAppSdk'
import { FixedTermMarketsSearchInput } from './fixedTermMarketsSearchInput'
import { FixedTermMarketsSortSelect } from './fixedTermMarketsSortSelect'
import { useFixedTermMarketsFilterData } from './useFixedTermMarketsFilterData'

interface FixedTermMarketsFiltersModalProps {
  markets: FixedTermMarket[]
  numberOfMarkets: number
  className?: string
}

export const FixedTermMarketsFiltersModal = ({
  markets,
  numberOfMarkets,
  className,
}: FixedTermMarketsFiltersModalProps) => {
  const { activeFiltersCount, clearFilters } = useFixedTermMarketsSearchState()

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="default" size="default" className={cn('relative rounded-xl bg-page ring-inset', className)}>
          <FilterIcon size={24} />
          <span className="sr-only">Open filters</span>
          {activeFiltersCount > 0 && (
            <p className="pointer-events-none absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-center font-bold text-page leading-none ring-2 ring-page [font-size:10px]">
              <span className="sr-only">Number of filters applied</span>
              <span>{activeFiltersCount}</span>
            </p>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent position="right">
        <DialogBody className="bg-page">
          <DialogHeaderWrapper className="px-5 md:pt-8">
            <DialogHeader>
              <DialogTitle>Filters</DialogTitle>
            </DialogHeader>
            <Separator />
          </DialogHeaderWrapper>
          <DialogScrollArea>
            <DialogContentWrapper className="px-5 pt-4">
              <div className="flex flex-wrap gap-2">
                <FixedTermMarketsSearchInput numberOfMarkets={numberOfMarkets} className="w-fit flex-1 basis-48" />
                <FixedTermMarketsSortSelect className="flex-1 justify-start" modal />
              </div>
              <FixedTermMarketsFilterAccordions markets={markets} />
              {activeFiltersCount > 0 && (
                <Button variant="input" size="md" onClick={clearFilters} className="gap-2">
                  <PlusIcon size={20} className="rotate-45" />
                  Clear filters
                </Button>
              )}
            </DialogContentWrapper>
          </DialogScrollArea>
          <DialogFooter className="px-5">
            <DialogClose asChild>
              <Button variant="primary" size="md" className="w-full">
                Confirm
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

const FixedTermMarketsFilterAccordions = ({ markets }: Pick<FixedTermMarketsFiltersModalProps, 'markets'>) => {
  const { assetFilterProps, protocolFilterProps, networkFilterProps } = useFixedTermMarketsFilterData(markets)

  return (
    <Accordion type="multiple">
      <AccordionItem value="asset">
        <FilterAccordionTrigger name="asset" />
        <FilterAccordionContent>
          <FilterListContent {...assetFilterProps} scrollClassName="max-h-48" />
        </FilterAccordionContent>
      </AccordionItem>
      <AccordionItem value="underlying-protocol">
        <FilterAccordionTrigger name="underlying protocol" />
        <FilterAccordionContent>
          <FilterListContent {...protocolFilterProps} scrollClassName="max-h-48" />
        </FilterAccordionContent>
      </AccordionItem>
      <AccordionItem value="network">
        <FilterAccordionTrigger name="network" />
        <FilterAccordionContent>
          <FilterListContent {...networkFilterProps} scrollClassName="max-h-48" />
        </FilterAccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
